import { HttpRequest, HttpMethod, axiosInstance } from "./http";

// SellerProvider — API calls for the "seller" backoffice area (epic #45).
// Mirrors StorefrontProvider. The 2C2P payment report endpoints (#50) read live
// from the App PG via the backoffice /api/v1/bo/seller/* routes (gated IsSeller).
class SellerProvider extends HttpRequest {
    // 2C2P payment report (#50). params: { date_from, date_to, channel, status }
    paymentReport(params) {
        return this.request(HttpMethod.Get, "/api/v1/bo/seller/payment-report", params);
    }

    // Export the same filtered report as a downloadable CSV blob.
    paymentReportExport(params) {
        return axiosInstance.get("/api/v1/bo/seller/payment-report", {
            params: Object.assign({}, params, { export: "csv" }),
            responseType: "blob"
        });
    }

    // Orders list (#48), hybrid live read from App PG.
    // params: { channel, status, date_from, date_to, q, limit, offset, page,
    //           payment_method_type, packing_back_day }
    // `payment_method_type` (0 = all) filters the rows; `packing_back_day`
    // (1..30, default 5) only sizes the `packing_preview` block in the response
    // — the per-channel ready-to-cut counts the chip row renders. Both are
    // echoed back in `packing_preview` so the screen can prove it counted with
    // the same window the cut will use (design 2026-10-04 §4.1).
    orders(params) {
        return this.request(HttpMethod.Get, "/api/v1/bo/seller/orders", params);
    }

    // Single order detail (header + items + payment + address + BO linkage).
    orderDetail(uuid) {
        return this.request(HttpMethod.Get, `/api/v1/bo/seller/orders/${uuid}`, {});
    }

    // Cancel one App order from the seller page (spec 2026-10-16 seller-order-
    // cancel-actor-reason §4; gated IsSeller + IsFullOrder). The linked BO rows
    // are cancelled with the same reason + actor.
    // payload: { bo_cancel_reason_id: 1|2|3|4|6, cancel_reason: "…" (≤ 255) }
    cancelOrder(uuid, payload) {
        return this.request(HttpMethod.Post, `/api/v1/bo/seller/orders/${uuid}/cancel`, payload);
    }

    // DEPRECATED (design 2026-10-04 §4.4) — channel-scoped pack cut-off (#49):
    // the 3 App channels + eligible counts. The seller orders page no longer
    // calls this; it cuts every channel in one round via packingRound() below.
    // Kept because the route still exists for a channel-only cut, and because
    // its resolver bug was fixed rather than removed.
    // Optional params: { packing_order_back_day } scopes the count to the same
    // created_at window the cut will use, so eligible_count matches the batch.
    channels(params) {
        return this.request(HttpMethod.Get, "/api/v1/bo/seller/channels", params || {});
    }

    // DEPRECATED (design 2026-10-04 §4.4) — cut a single channel-scoped round.
    // payload: { channel, bo_payment_method_type_id, shipping_provider_id, packing_order_back_day }
    cutOff(payload) {
        return this.request(HttpMethod.Post, "/api/v1/bo/seller/cut-off", payload);
    }

    // Cut ONE packing round covering web + member + app (design 2026-10-04 §4.2).
    // payload: { back_day, bo_payment_method_type_id, idempotency_key }
    // No channel and no carrier: the round is selected by platform_id = 9, and
    // the pack room assigns the carrier. `idempotency_key` is mandatory — the
    // server holds it in Redis for 120s and answers 409 errors.cutoff_in_progress
    // to the second of two fast clicks, so one double-click yields one round.
    packingRound(payload) {
        return this.request(HttpMethod.Post, "/api/v1/bo/seller/orders/packing-round", payload);
    }

    // Past rounds cut from this page (design 2026-10-04 §4.3).
    // params: { date_from, date_to, limit, page }
    packingRounds(params) {
        return this.request(HttpMethod.Get, "/api/v1/bo/seller/orders/packing-rounds", params || {});
    }

    // Spec 2026-10-14 §3.2 — seller mounts of the tracking-failure recovery.
    // Without bo_packing_id: every failed order of the seller's rounds in the
    // last 14 days (the banner source).
    trackingFailures(params) {
        return this.request(HttpMethod.Get, "/api/v1/bo/seller/orders/tracking-failures", params || {});
    }

    // payload: { bo_packing_id, order_ids }
    trackingRetry(payload) {
        return this.request(HttpMethod.Post, "/api/v1/bo/seller/orders/tracking-retry", payload);
    }

    // Platform 9 orders only.
    updateRecipient(id, payload) {
        return this.request(HttpMethod.Put, `/api/v1/bo/seller/orders/recipient/${id}`, payload);
    }

    stuckNoRound(params) {
        return this.request(HttpMethod.Get, "/api/v1/bo/seller/orders/stuck-no-round", params || {});
    }

    // payload: { order_ids } (≤ 200)
    revertAwaitingShipment(payload) {
        return this.request(HttpMethod.Post, "/api/v1/bo/seller/orders/revert-awaiting-shipment", payload);
    }
}

export default SellerProvider;
